import {CurrentRepositoryProvider} from '@github-ui/current-repository'
import {activityIndexPath} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useNavigate} from '@github-ui/react-router'
import {Heading, Link, PageLayout, Pagination, Text} from '@primer/react'
import {Box} from '@primer/styled-react'
import React from 'react'

import {ActivityBlankSlate} from '../components/ActivityBlankSlate'
import {ActivityCard} from '../components/ActivityCard'
import {ActivityFilterBar} from '../components/ActivityFilterBar'
import {useBaseUrl} from '../contexts/BaseUrlContext'
import {PerPageProvider} from '../contexts/PerPageContext'
import type {ActivityIndexPagePayload} from '../types/activity-types'

export default function ActivityIndex() {
  const navigate = useNavigate()
  const baseUrl = useBaseUrl()

  // When baseUrl is present means we're rendering this page from Stafftools
  // And then we want the full SHA instead of the shortened one
  const showFullShas = baseUrl ? true : false

  const {repo, activityList, refInfo, feedbackUrl} = useRoutePayload<ActivityIndexPagePayload>()

  // The pagination control assumes we know a current page and number of pages
  // With pushes, we don't pre-fetch that data. We only check to see if there is a
  // a previous page of pushes or a next page of pushes. So we will make use of pageCount
  // and currentPage to control whether the previous or next page links are enabled.

  // If there are additional pages of pushes, we will use a page count of 3 to mimic a next/previous page.
  // If there aren't additional pages of pushes, then we only have 1 page.
  const pageCount = activityList.hasNextPage || activityList.hasPreviousPage ? 3 : 1

  // Since we are using 3 pages to represent existance of a next or previous page, we will set currentPage to 1
  // if there is no previous page, which will disable the "previous" button. If there is no next page
  // then we will set currentPage to 3, which will disable the "next" button. Otherwise we will set currentPage
  // to 2 which will enable both buttons.
  // If there is only one page of pushes, pageCount will be 1. There will be no previous page, which will set
  // currentPage to 1. This will disable both the "previous" and "next" buttons.
  const currentPage = !activityList.hasPreviousPage ? 1 : !activityList.hasNextPage ? 3 : 2

  const onPageChange = React.useCallback(
    (e: React.MouseEvent, p: number) => {
      window.scrollTo(0, 0)
      // If the user clicks "previous" then we will get p = 1 and currentPage = 2 or p = 2 and currentPage = 3.
      // If the user clicks "next" then we will get p = 2 and currentPage = 1 or p = 3 and currentPage = 2.
      navigate(
        activityIndexPath({
          repo,
          baseUrl,
          branch: refInfo.name,
          filter: activityList,
          pagination:
            p < currentPage
              ? {before: activityList.startCursor || activityList.cursor}
              : {after: activityList.endCursor || activityList.cursor},
          perPage: activityList.perPage,
        }),
      )

      e.preventDefault()
    },
    [navigate, repo, baseUrl, refInfo.name, activityList, currentPage],
  )

  return (
    <CurrentRepositoryProvider repository={repo}>
      <PerPageProvider perPage={activityList.perPage}>
        <PageLayout sx={{minHeight: '500px'}}>
          <PageLayout.Header divider="line" sx={{mb: ['3', '3', '3', '3']}}>
            <Heading as="h1" sx={{fontSize: 4, fontWeight: 'normal', mt: 2, lineHeight: 1}}>
              Activity
            </Heading>
          </PageLayout.Header>
          <PageLayout.Content>
            <ActivityFilterBar repo={repo} refInfo={refInfo} filter={activityList} />
            {/* Outer box holding pushes list */}
            <Box
              data-hpc
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                borderColor: 'border.default',
                borderStyle: 'solid',
                borderWidth: 1,
                mt: 3,
              }}
            >
              {activityList.items.length > 0 ? (
                <>
                  {activityList.items.map((push, index) => (
                    <ActivityCard
                      // eslint-disable-next-line @eslint-react/no-array-index-key
                      key={index}
                      push={push}
                      repo={repo}
                      showRefInfo={refInfo.name === ''}
                      showFullShas={showFullShas}
                    />
                  ))}
                </>
              ) : (
                <ActivityBlankSlate />
              )}
            </Box>
          </PageLayout.Content>
          <PageLayout.Footer>
            {feedbackUrl ? (
              <div>
                <Link href={feedbackUrl} target="_blank" sx={{ml: 2}}>
                  <Text sx={{fontSize: 1, display: ['none', 'inline', 'inline']}}>Share feedback about this page</Text>
                  <Text sx={{fontSize: 0, display: ['inline', 'none', 'none']}}>Share feedback</Text>
                </Link>
              </div>
            ) : null}
            {(activityList.hasNextPage || activityList.hasPreviousPage) && (
              <Pagination
                pageCount={pageCount}
                currentPage={currentPage}
                onPageChange={onPageChange}
                showPages={false}
              />
            )}
          </PageLayout.Footer>
        </PageLayout>
      </PerPageProvider>
    </CurrentRepositoryProvider>
  )
}

try{ ActivityIndex.displayName ||= 'ActivityIndex' } catch {}